/* @flow */
import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import _ from 'lodash';
import Autosuggest from 'react-autosuggest';

import styles from './styles.module.scss';
import Tooltip from '../Tooltip';
import searchImg from '../../assets/images/search.svg';
import resetImg from '../../assets/images/reset.svg';
import titleImg from '../../assets/images/title.svg';
import peopleImg from '../../assets/images/people.svg';
import tagsImg from '../../assets/images/tags.svg';
import actions from '../../redux/actions';
import { getCategories } from '../../redux/reducers/categories';
import commonUtils from '../../utils/common';
import stringUtils from '../../utils/string';

import type {
  Translate,
  SearchField,
  SearchFilter,
  SearchParams,
  ReduxState,
  Fetchable,
  Category,
} from '../../types';

const mapStateToProps = (state: ReduxState, ownProps) => {
  return {
    ...ownProps,
    params: state.app.searchParams,
    paramsHistory: state.app.searchHistory,
    categories: getCategories(state),
  };
};

type Props = {
  params: SearchParams,
  paramsHistory: SearchParams[],
  placeholder: string,
  onChange?: (text: string) => any,
  onSubmit?: (params: SearchParams) => any,
  onFlagsUpdate?: ({ [SearchField]: boolean }) => any,
  onFiltersUpdate?: ({ [SearchFilter]: string[] }) => any,
  onSearchReset?: () => mixed,
  categories: Fetchable<Category[]>,
  t: Translate,
};

const ToolbarSearchBox = (props: Props): React$Element<any> => {
  const {
    params,
    paramsHistory,
    placeholder = '',
    onFlagsUpdate = (flags: any) => null,
    onFiltersUpdate = (filters: any) => null,
    onSubmit = (params: SearchParams) => null,
    onChange = (text: string) => null,
    onSearchReset = () => null,
    t,
  } = props;

  const [searchText, setSearchText] = useState('');

  const isFlagActive = (field: SearchField) => {
    return !!params.flags[field];
  };

  const handleKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      onChange(searchText);
      onSubmit({ ...params, text: searchText });
    }
  };

  const handleSearchClick = () => {
    onChange(searchText);
    onSubmit({ ...params, text: searchText });
  };

  const handleSearchReset = () => {
    setSearchText('');
    onSearchReset();
  };

  const getSuggestions = value => {
    let suggestions = [];
    const isFiltering = (value || '').trim().length > 0;
    if (isFiltering) {
      suggestions = paramsHistory.filter(paramsItem =>
        stringUtils.fuzzyMatch(paramsItem.text, value)
      );
    } else {
      suggestions = paramsHistory;
    }
    return _.take(suggestions, 10);
  };

  const getSuggestionValue = (params: SearchParams) => params;

  const categoryIdToName = (id: number) => {
    const { categories } = props;
    const category = (categories.data || []).find(c => c.id === id);
    return _.get(category, 'description', '');
  };

  const renderSuggestion = (params: SearchParams) => {
    const { filters = {} } = params;
    const activeFiltersNames = Object.keys(filters).filter(filterName => {
      const filterValues = params.filters[filterName];
      if (_.isArray(filterValues) && filterValues.length > 0) {
        return true;
      }
      return false;
    });
    return (
      <div className={styles.inputSuggestion}>
        <div className={styles.inputSuggestionText}>{params.text}</div>
        <div className={styles.inputSuggestionOptions}>
          <div className={styles.inputSuggestionFilters}>
            {activeFiltersNames.map(filterName => (
              <div key={filterName} className={styles.inputSuggestionFilter}>
                <div className={styles.inputSuggestionFilterName}>
                  {stringUtils.capitalizeString(filterName)}
                </div>
                {filters[filterName].map(filterValue => (
                  <div
                    key={`${filterName}-${filterValue}`}
                    className={styles.inputSuggestionFilterValue}
                  >
                    {filterName === 'categories' ? categoryIdToName(filterValue) : filterValue}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className={styles.inputSuggestionFlags}>
            <Tooltip title={t('TITLE')}>
              <img
                src={titleImg}
                className={!!params.flags.title ? styles.flagActive : styles.flagDisabled}
                alt={t('TITLE')}
              />
            </Tooltip>
            <Tooltip title={t('TAGS')}>
              <img
                src={tagsImg}
                className={!!params.flags.tags ? styles.flagActive : styles.flagDisabled}
                alt={t('TAGS')}
              />
            </Tooltip>
            <Tooltip title={t('PEOPLE')}>
              <img
                src={peopleImg}
                className={!!params.flags.people ? styles.flagActive : styles.flagDisabled}
                alt={t('PEOPLE')}
              />
            </Tooltip>
          </div>
        </div>
      </div>
    );
  };

  const handleOnSuggestionSelected = async (
    event,
    { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }
  ) => {
    if (typeof suggestionValue.text === 'string') {
      setSearchText(suggestionValue.text);
      onChange(suggestionValue.text);
      await commonUtils.delay(0);
      onFlagsUpdate(suggestionValue.flags);
      onFiltersUpdate(suggestionValue.filters);
      onSubmit({ ...params, text: suggestionValue.text });
    }
  };

  const handleToggleFlag = (flag: SearchField) => {
    onFlagsUpdate({ ...(params.flags || {}), [flag]: !isFlagActive(flag) });
  };

  const inputProps = {
    className: styles.input,
    placeholder: placeholder,
    type: 'text',
    autoComplete: 'off',
    autoCorrect: 'off',
    autoCapitalize: 'off',
    spellCheck: 'false',
    value: searchText || '',
    onKeyDown: handleKeyDown,
    onChange: e => typeof e.target.value === 'string' && setSearchText(e.target.value),
  };

  const canResetSearch =
    !!searchText || isFlagActive('title') || isFlagActive('tags') || isFlagActive('people');

  return (
    <div className={styles.container}>
      <div className={styles.inputContainer}>
        <Autosuggest
          suggestions={getSuggestions(searchText)}
          onSuggestionsFetchRequested={() => {}}
          onSuggestionsClearRequested={() => {}}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          shouldRenderSuggestions={value => true}
          inputProps={inputProps}
          onSuggestionSelected={handleOnSuggestionSelected}
        />
      </div>
      <img
        src={searchImg}
        className={styles.searchImg}
        alt={t('SEARCH')}
        onClick={handleSearchClick}
      />
      <div className={styles.flags}>
        {canResetSearch && (
          <img
            src={resetImg}
            className={styles.reset}
            onClick={handleSearchReset}
            alt={t('RESET')}
          />
        )}
        <Tooltip title={t('SEARCH_TITLES')}>
          <img
            src={titleImg}
            className={isFlagActive('title') ? styles.flagActive : styles.flagDisabled}
            onClick={() => handleToggleFlag('title')}
            alt={t('SEARCH_TITLES')}
          />
        </Tooltip>
        <Tooltip title={t('SEARCH_TAGS')}>
          <img
            src={tagsImg}
            className={isFlagActive('tags') ? styles.flagActive : styles.flagDisabled}
            onClick={() => handleToggleFlag('tags')}
            alt={t('SEARCH_TAGS')}
          />
        </Tooltip>
        <Tooltip title={t('SEARCH_PEOPLE')}>
          <img
            src={peopleImg}
            className={isFlagActive('people') ? styles.flagActive : styles.flagDisabled}
            onClick={() => handleToggleFlag('people')}
            alt={t('SEARCH_PEOPLE')}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default withTranslation()(connect(mapStateToProps, actions)(ToolbarSearchBox));
